import { getHasAnyProjects } from "@frontend-shared/utils";
import { memo, useCallback, useState } from "react";
import { Outlet } from "react-router-dom";

import { useNotificationSocket } from "@/components/notifications/hooks/use-notification-socket";
import { NotificationsDrawer } from "@/components/notifications/NotificationsDrawer";
import { NotificationsProvider } from "@/components/notifications/provider/NotificationsProvider";
import { FloatingTransferPopup } from "@/components/TransferPopup";
import { SidebarInset, SidebarProvider } from "@/components/ui/sidebar";
import { CopyrightLineContext } from "@/contexts/CopyrightLineContext";
import { HeaderSlotContext } from "@/contexts/HeaderSlotContext";
import { useAuth } from "@/hooks/use-auth";
import { useFaviconBadge } from "@/hooks/use-favicon-badge";
import { useIsMeetingDetailsRoute } from "@/hooks/use-is-meeting-details-route";
import TransferPopupProvider from "@/providers/TransferPopupProvider";

import { CopyrightNotice } from "./CopyrightNotice";
import { GlobalSearch } from "./global-search/GlobalSearch";
import { useGlobalSearchShortcut } from "./global-search/hooks/use-global-search-shortcut";
import { Header } from "./Header";
import { MainContent } from "./MainContent";
import { MainSidebar } from "./MainSidebar";
import { SkipToMainContent } from "./SkipToMainContent";

const AppLayoutComponent = (): JSX.Element => {
  const [isSearchChatsOpen, setIsSearchChatsOpen] = useState(false);
  // NOTE: NSAI-5822 — the header's breadcrumb slot. Held as state rather than a
  // ref so the context re-renders its consumers once the element exists;
  // `Toolbar` portals into it from inside the routed page below.
  const [breadcrumbSlot, setBreadcrumbSlot] = useState<HTMLDivElement | null>(
    null,
  );
  // NOTE: Set while a page prints the copyright on its own bottom line (see
  // `CopyrightLineContext`), so the shell's line steps aside.
  const [isCopyrightLineClaimed, setIsCopyrightLineClaimed] = useState(false);
  const { projects, user } = useAuth();
  const isMeetingDetailsRoute = useIsMeetingDetailsRoute();

  useNotificationSocket({ isAuthenticated: Boolean(user) });

  // NOTE: Paints the unread count as a badge on top of the browser favicon
  // (alongside the "(N) " prefix on document.title from useDocumentTitle).
  // Mounted in the authenticated layout so unauthenticated pages don't try
  // to read the cache. Same data source as UserMenu's avatar badge.
  useFaviconBadge();

  const hasAnyProjects = getHasAnyProjects(projects);
  // NOTE: NSAI-6589 — the global search palette is there for every signed-in
  // user with a project; only its semantic message section still depends on a
  // search embedding model (see useGlobalSearchChats).
  const handleToggleSearch = useCallback((): void => {
    setIsSearchChatsOpen((isOpen) => !isOpen);
  }, []);
  useGlobalSearchShortcut({
    isEnabled: hasAnyProjects,
    onToggle: handleToggleSearch,
  });

  return (
    <NotificationsProvider>
      <SidebarProvider>
        <SkipToMainContent />
        {hasAnyProjects && (
          <GlobalSearch
            isOpen={isSearchChatsOpen}
            onOpenChange={setIsSearchChatsOpen}
          />
        )}
        {/* NOTE: NSAI-7067 — inside the layout, so the card's dismissals and
            its dock registry are shared by every page under it. */}
        <TransferPopupProvider>
          <HeaderSlotContext.Provider value={breadcrumbSlot}>
            <div className="h-screen w-screen flex">
              {hasAnyProjects && <MainSidebar />}
              <SidebarInset>
                <Header
                  isSearchChatsOpen={isSearchChatsOpen}
                  setIsSearchChatsOpen={setIsSearchChatsOpen}
                  breadcrumbSlotRef={setBreadcrumbSlot}
                />
                <CopyrightLineContext.Provider
                  value={setIsCopyrightLineClaimed}
                >
                  {/* NOTE: The meeting page ends in the recording play bar,
                    whose far end holds its speed and download controls, so
                    the copyright stands down there and the account menu
                    carries it instead. */}
                  <MainContent
                    isCopyrightLineShown={
                      !isMeetingDetailsRoute && !isCopyrightLineClaimed
                    }
                  >
                    <Outlet />
                  </MainContent>
                </CopyrightLineContext.Provider>
                {/* NOTE: While a page holds the end of its own bottom line for
                  the copyright (the started chat, see `ChatDisclaimer`), it is
                  drawn here, 16px in like the shell's line and on that
                  line — over space the page keeps empty for it. Drawn from the
                  shell because the chat pane stops short of the corner. */}
                {isCopyrightLineClaimed && !isMeetingDetailsRoute && (
                  <CopyrightNotice
                    testId="app-copyright"
                    className="pointer-events-none absolute bottom-3 end-4 hidden whitespace-nowrap leading-4 text-muted-foreground lg:block"
                  />
                )}
                {/* NOTE: NSAI-7067 — a transfer started on the Uploads page
                  follows the user here. Raised on the meeting page, which
                  ends in its own playback bar. */}
                <FloatingTransferPopup isRaised={isMeetingDetailsRoute} />
              </SidebarInset>
            </div>
          </HeaderSlotContext.Provider>
        </TransferPopupProvider>
        <NotificationsDrawer />
      </SidebarProvider>
    </NotificationsProvider>
  );
};

export const AppLayout = memo(AppLayoutComponent);
